import type { SiteManifest } from "./domain";

export const lottoPreviewManifest: SiteManifest = {
  appId: "lotto-map",
  slug: "lotto-map",
  displayName: "로또명당 지도",
  locale: "ko-KR",
  indexing: "NOINDEX",
  publicationMode: "FULL_WEB_PWA",
  releaseStatus: "PREVIEW",
  userLoginEnabled: false,
  previewNotice: "개발 테스트 환경입니다. 입력한 복권·게시글·이용권은 이 브라우저에서만 사용되며 실제 계정·결제에는 반영되지 않습니다.",
  latestDataLabel: "검증된 데이터 기준 · 2026년 8월 19일 09:00",
  modules: [
    { id: "lotto.map.v1", label: "판매점 지도", access: "PUBLIC", enabled: true },
    { id: "lotto.results.v1", label: "당첨결과", access: "PUBLIC", enabled: true },
    { id: "lotto.picks.v1", label: "번호추천", access: "PUBLIC", enabled: true },
    { id: "lotto.vault.v1", label: "보관함", access: "PUBLIC", enabled: true },
    { id: "lotto.community.v1", label: "커뮤니티", access: "MEMBER", enabled: false },
  ],
};

export async function loadSiteManifest(appId: string, fallback: SiteManifest = lottoPreviewManifest): Promise<SiteManifest> {
  // The NAS preview is deliberately a static public surface.  It can provide
  // only a non-secret, host-scoped map client identifier here; user sessions
  // and any private configuration continue to come from the future Web BFF.
  const staticManifest = await fetchStaticManifest(appId);
  if (staticManifest) return staticManifest;

  const response = await fetch(`/web/api/v1/apps/${encodeURIComponent(appId)}/site-manifest`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (response.status === 404 || response.status === 503) return fallback;
  if (!response.ok) throw new Error("site_manifest_unavailable");
  const payload: unknown = await response.json();
  if (!isSiteManifest(payload) || payload.appId !== appId) throw new Error("invalid_site_manifest");
  return payload;
}

async function fetchStaticManifest(appId: string): Promise<SiteManifest | null> {
  try {
    const response = await fetch("/site-manifest.json", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error("static_site_manifest_unavailable");
    const payload: unknown = await response.json();
    if (!isSiteManifest(payload) || payload.appId !== appId) throw new Error("invalid_static_site_manifest");
    return payload;
  } catch (error) {
    // The BFF fallback preserves local development and controlled cutovers.
    // A malformed static manifest must not silently become application data.
    if (error instanceof Error && error.message === "invalid_static_site_manifest") throw error;
    return null;
  }
}

function isSiteManifest(value: unknown): value is SiteManifest {
  if (!isRecord(value)
    || typeof value.appId !== "string"
    || typeof value.slug !== "string"
    || typeof value.displayName !== "string"
    || typeof value.locale !== "string"
    || (value.indexing !== "NOINDEX" && value.indexing !== "INDEX")
    || !isPublicationMode(value.publicationMode)
    || (value.releaseStatus !== "PREVIEW" && value.releaseStatus !== "ACTIVE")
    || typeof value.userLoginEnabled !== "boolean"
    || typeof value.latestDataLabel !== "string"
    || !Array.isArray(value.modules)
    || !value.modules.every(isWebModule)) return false;
  return value.mapProvider === undefined || value.mapProvider === null || (isRecord(value.mapProvider) && value.mapProvider.kind === "NAVER" && typeof value.mapProvider.clientId === "string");
}

function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null; }
function isPublicationMode(value: unknown): value is SiteManifest["publicationMode"] { return value === "MOBILE_ONLY" || value === "PUBLIC_SITE" || value === "FULL_WEB_PWA"; }
function isWebModule(value: unknown): boolean {
  return isRecord(value)
    && typeof value.id === "string"
    && typeof value.label === "string"
    && (value.access === "PUBLIC" || value.access === "MEMBER" || value.access === "ENTITLEMENT" || value.access === "CREDIT" || value.access === "QUOTA")
    && typeof value.enabled === "boolean";
}
